import { Card, Button, Table, Flex, Typography, Empty, theme, Tag, App } from 'antd';
import { GlobalOutlined, EditOutlined, LinkOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Company } from '@/api/auth/interfaces';
import { RowActionsMenu, type RowAction } from '@/components/shared/RowActionsMenu';
import { DateTimeCell } from '@/components/shared/DateTimeCell';
import { IconCardTitle } from '@/components/ui/IconCardTitle';
import { useResponsivePinnedColumn } from '@/hooks/useResponsivePinnedColumn';

const { Text } = Typography;

export interface TenantCompaniesTableProps {
  companies: Company[];
  onAddCompany: () => void;
  onEditCompany: (company: Company) => void;
  onGenerateInviteLink: (company: Company) => void;
  onDeleteCompany?: (company: Company) => void;
  loading?: boolean;
}

/**
 * Companies of a tenant with per-row edit, invite-link and delete actions.
 */
export function TenantCompaniesTable({
  companies,
  onAddCompany,
  onEditCompany,
  onGenerateInviteLink,
  onDeleteCompany,
  loading = false
}: TenantCompaniesTableProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const { modal } = App.useApp();

  // Pinned columns would cover most of a phone screen, so only pin them from md up
  const pinColumn = useResponsivePinnedColumn();

  const getRowActions = (): RowAction[] => {
    const actions: RowAction[] = [
      { key: 'edit', icon: <EditOutlined />, label: t('tenants.detail.edit') },
      { key: 'invite', icon: <LinkOutlined />, label: t('tenants.detail.generateInviteLink') }
    ];
    if (onDeleteCompany) {
      actions.push({ key: 'delete', icon: <DeleteOutlined />, label: t('tenants.detail.deleteCompany.tooltip'), danger: true });
    }
    return actions;
  };

  const handleActionClick = (company: Company, key: string) => {
    if (key === 'edit') onEditCompany(company);
    else if (key === 'invite') onGenerateInviteLink(company);
    else if (key === 'delete' && onDeleteCompany) {
      modal.confirm({
        title: t('tenants.detail.deleteCompany.confirmTitle'),
        content: t('tenants.detail.deleteCompany.confirmDesc'),
        okText: t('tenants.detail.deleteCompany.ok'),
        cancelText: t('tenants.detail.deleteCompany.cancel'),
        okButtonProps: { danger: true },
        onOk: () => onDeleteCompany(company)
      });
    }
  };

  const columns = [
    {
      title: t('tenants.detail.table.companyName'),
      key: 'name',
      width: 200,
      fixed: pinColumn('left'),
      render: (_: unknown, company: Company) => (
        <Flex align="center" gap={token.marginSM} style={{ minWidth: 0 }}>
          <Flex
            align="center"
            justify="center"
            style={{
              width: token.controlHeight,
              height: token.controlHeight,
              borderRadius: token.borderRadius,
              background: token.colorPrimaryBg,
              color: token.colorPrimary,
              flexShrink: 0
            }}
          >
            <GlobalOutlined />
          </Flex>
          <Text strong ellipsis={{ tooltip: true }} style={{ maxWidth: 220 }}>
            {company.name}
          </Text>
        </Flex>
      )
    },
    {
      title: t('tenants.detail.table.domain'),
      dataIndex: 'domain',
      key: 'domain',
      width: 160,
      ellipsis: true,
      render: (domain: string | null) =>
        domain ? <Text type="secondary">{domain}</Text> : '—'
    },
    {
      title: t('tenants.detail.table.city'),
      dataIndex: 'city',
      key: 'city',
      width: 120,
      ellipsis: true,
      render: (city: string | null) =>
        city ? <Text>{city}</Text> : '—'
    },
    {
      title: t('tenants.detail.table.country'),
      dataIndex: 'country',
      key: 'country',
      width: 100,
      ellipsis: true,
      render: (country: string | null) =>
        country ? <Text>{country}</Text> : '—'
    },
    {
      title: t('tenants.detail.table.email'),
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
      render: (email: string | null) =>
        email ? <Text type="secondary">{email}</Text> : '—'
    },
    {
      title: t('tenants.detail.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => <DateTimeCell value={date} />
    },
    {
      title: t('tenants.detail.table.actions'),
      key: 'actions',
      width: 72,
      fixed: pinColumn('right'),
      render: (_: unknown, company: Company) => (
        <RowActionsMenu actions={getRowActions()} onSelect={(key) => handleActionClick(company, key)} />
      )
    }
  ];

  return (
    <Card
      title={
        <IconCardTitle
          icon={<GlobalOutlined />}
          title={t('tenants.detail.companies')}
          extra={!loading && <Tag style={{ marginInlineEnd: 0 }}>{companies.length}</Tag>}
        />
      }
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={onAddCompany}>
          {t('tenants.detail.addCompany')}
        </Button>
      }
      styles={{ body: { padding: 0 } }}
    >
      <Table
        dataSource={companies}
        rowKey="id"
        columns={columns}
        pagination={false}
        loading={loading}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('tenants.detail.noCompanies')} />
        }}
      />
    </Card>
  );
}
